import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  // General
  CloudIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon,
  CreditCardIcon,
  BriefcaseIcon,
  CogIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  BoltIcon,
  ClipboardDocumentCheckIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  LightBulbIcon,
  ArrowsPointingOutIcon,
  ServerIcon,
  UserGroupIcon,
  ChartBarIcon,
  BeakerIcon,
  RocketLaunchIcon,
  Square3Stack3DIcon,
  // Additional icons
  StarIcon,
  HeartIcon,
  FireIcon,
  FlagIcon,
  TagIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  BookOpenIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  CodeBracketIcon,
  CommandLineIcon,
  ComputerDesktopIcon,
  CpuChipIcon,
  CubeIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  EyeIcon,
  FingerPrintIcon,
  FolderIcon,
  GiftIcon,
  HomeIcon,
  KeyIcon,
  LinkIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MegaphoneIcon,
  MicrophoneIcon,
  MusicalNoteIcon,
  PaintBrushIcon,
  PaperAirplaneIcon,
  PhotoIcon,
  PlayIcon,
  PresentationChartLineIcon,
  PrinterIcon,
  PuzzlePieceIcon,
  QrCodeIcon,
  ScaleIcon,
  ScissorsIcon,
  ShoppingCartIcon,
  SignalIcon,
  SpeakerWaveIcon,
  SunIcon,
  TableCellsIcon,
  TruckIcon,
  TvIcon,
  VideoCameraIcon,
  WifiIcon,
  WindowIcon,
} from '@heroicons/react/24/outline';
import { FieldContainer } from './FormField';

// Icon mapping - comprehensive list
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  // Original icons
  cloud: CloudIcon,
  mobile: DevicePhoneMobileIcon,
  globe: GlobeAltIcon,
  'credit-card': CreditCardIcon,
  briefcase: BriefcaseIcon,
  cog: CogIcon,
  'currency-dollar': CurrencyDollarIcon,
  'shield-check': ShieldCheckIcon,
  bolt: BoltIcon,
  'clipboard-check': ClipboardDocumentCheckIcon,
  sparkles: SparklesIcon,
  wrench: WrenchScrewdriverIcon,
  'light-bulb': LightBulbIcon,
  'arrows-pointing-out': ArrowsPointingOutIcon,
  server: ServerIcon,
  'user-group': UserGroupIcon,
  'chart-bar': ChartBarIcon,
  beaker: BeakerIcon,
  rocket: RocketLaunchIcon,
  stack: Square3Stack3DIcon,
  // Additional icons
  star: StarIcon,
  heart: HeartIcon,
  fire: FireIcon,
  flag: FlagIcon,
  tag: TagIcon,
  clock: ClockIcon,
  warning: ExclamationTriangleIcon,
  badge: CheckBadgeIcon,
  book: BookOpenIcon,
  calendar: CalendarIcon,
  chat: ChatBubbleLeftRightIcon,
  database: CircleStackIcon,
  code: CodeBracketIcon,
  terminal: CommandLineIcon,
  desktop: ComputerDesktopIcon,
  cpu: CpuChipIcon,
  cube: CubeIcon,
  document: DocumentTextIcon,
  email: EnvelopeIcon,
  eye: EyeIcon,
  fingerprint: FingerPrintIcon,
  folder: FolderIcon,
  gift: GiftIcon,
  home: HomeIcon,
  key: KeyIcon,
  link: LinkIcon,
  lock: LockClosedIcon,
  search: MagnifyingGlassIcon,
  'map-pin': MapPinIcon,
  megaphone: MegaphoneIcon,
  microphone: MicrophoneIcon,
  music: MusicalNoteIcon,
  paint: PaintBrushIcon,
  'paper-airplane': PaperAirplaneIcon,
  photo: PhotoIcon,
  play: PlayIcon,
  'chart-line': PresentationChartLineIcon,
  printer: PrinterIcon,
  puzzle: PuzzlePieceIcon,
  qrcode: QrCodeIcon,
  scale: ScaleIcon,
  scissors: ScissorsIcon,
  cart: ShoppingCartIcon,
  signal: SignalIcon,
  speaker: SpeakerWaveIcon,
  sun: SunIcon,
  table: TableCellsIcon,
  truck: TruckIcon,
  tv: TvIcon,
  video: VideoCameraIcon,
  wifi: WifiIcon,
  window: WindowIcon,
};

interface IconOption {
  id: string;
  name: string;
}

const ICON_OPTIONS: IconOption[] = [
  // Original icons
  { id: 'cloud', name: 'Cloud' },
  { id: 'mobile', name: 'Mobile' },
  { id: 'globe', name: 'Globe' },
  { id: 'credit-card', name: 'Credit Card' },
  { id: 'briefcase', name: 'Briefcase' },
  { id: 'cog', name: 'Settings' },
  { id: 'currency-dollar', name: 'Currency' },
  { id: 'shield-check', name: 'Shield' },
  { id: 'bolt', name: 'Bolt' },
  { id: 'clipboard-check', name: 'Clipboard' },
  { id: 'sparkles', name: 'Sparkles' },
  { id: 'wrench', name: 'Wrench' },
  { id: 'light-bulb', name: 'Light Bulb' },
  { id: 'arrows-pointing-out', name: 'Expand' },
  { id: 'server', name: 'Server' },
  { id: 'user-group', name: 'Users' },
  { id: 'chart-bar', name: 'Bar Chart' },
  { id: 'beaker', name: 'Beaker' },
  { id: 'rocket', name: 'Rocket' },
  { id: 'stack', name: 'Stack' },
  // Additional icons
  { id: 'star', name: 'Star' },
  { id: 'heart', name: 'Heart' },
  { id: 'fire', name: 'Fire' },
  { id: 'flag', name: 'Flag' },
  { id: 'tag', name: 'Tag' },
  { id: 'clock', name: 'Clock' },
  { id: 'warning', name: 'Warning' },
  { id: 'badge', name: 'Badge' },
  { id: 'book', name: 'Book' },
  { id: 'calendar', name: 'Calendar' },
  { id: 'chat', name: 'Chat' },
  { id: 'database', name: 'Database' },
  { id: 'code', name: 'Code' },
  { id: 'terminal', name: 'Terminal' },
  { id: 'desktop', name: 'Desktop' },
  { id: 'cpu', name: 'CPU' },
  { id: 'cube', name: 'Cube' },
  { id: 'document', name: 'Document' },
  { id: 'email', name: 'Email' },
  { id: 'eye', name: 'Eye' },
  { id: 'fingerprint', name: 'Fingerprint' },
  { id: 'folder', name: 'Folder' },
  { id: 'gift', name: 'Gift' },
  { id: 'home', name: 'Home' },
  { id: 'key', name: 'Key' },
  { id: 'link', name: 'Link' },
  { id: 'lock', name: 'Lock' },
  { id: 'search', name: 'Search' },
  { id: 'map-pin', name: 'Location' },
  { id: 'megaphone', name: 'Megaphone' },
  { id: 'microphone', name: 'Microphone' },
  { id: 'music', name: 'Music' },
  { id: 'paint', name: 'Paint' },
  { id: 'paper-airplane', name: 'Send' },
  { id: 'photo', name: 'Photo' },
  { id: 'play', name: 'Play' },
  { id: 'chart-line', name: 'Line Chart' },
  { id: 'printer', name: 'Printer' },
  { id: 'puzzle', name: 'Puzzle' },
  { id: 'qrcode', name: 'QR Code' },
  { id: 'scale', name: 'Scale' },
  { id: 'scissors', name: 'Scissors' },
  { id: 'cart', name: 'Cart' },
  { id: 'signal', name: 'Signal' },
  { id: 'speaker', name: 'Speaker' },
  { id: 'sun', name: 'Sun' },
  { id: 'table', name: 'Table' },
  { id: 'truck', name: 'Truck' },
  { id: 'tv', name: 'TV' },
  { id: 'video', name: 'Video' },
  { id: 'wifi', name: 'WiFi' },
  { id: 'window', name: 'Window' },
];

interface IconPickerProps {
  label: string;
  value: string;
  onChange: (icon: string) => void;
  required?: boolean;
  error?: string;
}

export function IconPicker({
  label,
  value,
  onChange,
  required = false,
  error,
}: IconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleIconSelect = (iconId: string) => {
    onChange(iconId);
    setIsOpen(false);
    setSearch('');
  };

  // Filter icons based on search
  const filteredIcons = search
    ? ICON_OPTIONS.filter(icon =>
        icon.name.toLowerCase().includes(search.toLowerCase()) ||
        icon.id.toLowerCase().includes(search.toLowerCase())
      )
    : ICON_OPTIONS;

  // Find selected icon
  const selectedIcon = ICON_OPTIONS.find(icon => icon.id === value);
  const SelectedIconComponent = value ? ICON_MAP[value] : null;

  return (
    <FieldContainer
      label={label}
      htmlFor="icon-picker"
      required={required}
      error={error}
    >
      <div className="relative" ref={dropdownRef}>
        {/* Dropdown Trigger */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center gap-3 px-3 py-2 text-sm border rounded-md transition-colors bg-background-primary ${
            error
              ? 'border-red-500 focus:ring-red-500'
              : 'border-border-default hover:border-border-focus focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          }`}
        >
          {SelectedIconComponent ? (
            <SelectedIconComponent className="w-5 h-5 text-text-primary flex-shrink-0" />
          ) : (
            <div className="w-5 h-5 flex-shrink-0" />
          )}
          <span className="flex-1 text-left text-text-primary">
            {selectedIcon?.name || 'Select icon...'}
          </span>
          <ChevronDownIcon
            className={`w-4 h-4 text-text-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-background-elevated border border-border-default rounded-md shadow-lg z-50">
            {/* Search Input */}
            <div className="p-2 border-b border-border-subtle">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search icons..."
                className="w-full px-3 py-1.5 text-sm border border-border-default rounded-md bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Icon Grid */}
            <div className="p-2 max-h-64 overflow-y-auto">
              <div className="grid grid-cols-6 gap-1">
                {filteredIcons.map((icon) => {
                  const IconComponent = ICON_MAP[icon.id];
                  const isSelected = value === icon.id;
                  return (
                    <button
                      key={icon.id}
                      type="button"
                      onClick={() => handleIconSelect(icon.id)}
                      className={`flex items-center justify-center p-2 rounded transition-all ${
                        isSelected
                          ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-500'
                          : 'hover:bg-surface-hover text-text-secondary hover:text-text-primary'
                      }`}
                      title={icon.name}
                    >
                      {IconComponent && <IconComponent className="w-5 h-5" />}
                    </button>
                  );
                })}
              </div>
              {filteredIcons.length === 0 && (
                <div className="text-center py-4 text-sm text-text-secondary">
                  No icons found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </FieldContainer>
  );
}

// Helper function to get icon component by name
export function getIconComponent(
  iconName: string
): React.ComponentType<{ className?: string }> | null {
  return ICON_MAP[iconName] || null;
}

// Export ICON_MAP for use in other components
export { ICON_MAP };
